import { AbstractClient, CompositionClient } from "@/app/client";
import {
  Email,
  EmailAddressContextResponse,
  EmailContextForAddressResponse,
  EmailList,
  PageOptions,
} from "@/app/types";

export default class APIEmailsSub extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  getEmails = async (pageOptions: PageOptions): Promise<EmailList> => {
    let res = await this._fetch({
      url: "/emails/",
      method: "GET",
      params: pageOptions,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getEmail = async (emailId: string): Promise<Email> => {
    let res = await this._fetch({
      url: `/emails/${emailId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  batchFetchEmails = async (emailIds: string[]): Promise<Email[]> => {
    let res = await this._fetch({
      url: "/emails/batch-fetch",
      method: "POST",
      bodyMime: "application/json",
      body: { email_ids: emailIds },
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  createEmail = async (email: Partial<Email>): Promise<Email> => {
    let res = await this._fetch({
      url: "/emails",
      method: "POST",
      body: email,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  updateEmail = async (
    emailId: string,
    email: Partial<Email>,
  ): Promise<Email> => {
    let res = await this._fetch({
      url: `/emails/${emailId}`,
      method: "PUT",
      body: email,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  deleteEmail = async (emailId: string): Promise<void> => {
    await this._fetch({
      url: `/emails/${emailId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  // Media and attachment endpoints
  getEmailAttachments = async (emailId: string): Promise<any[]> => {
    let res = await this._fetch({
      url: `/emails/${emailId}/attachments`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  downloadEmailAttachment = async (
    emailId: string,
    attachmentName: string,
  ): Promise<Blob> => {
    let res = await this._fetch({
      url: `/emails/${emailId}/attachments/${encodeURIComponent(attachmentName)}/download`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.blob();
  };

  getEmailAttachmentThumbnail = async (
    emailId: string,
    attachmentName: string,
  ): Promise<Blob> => {
    let res = await this._fetch({
      url: `/emails/${emailId}/attachments/${encodeURIComponent(attachmentName)}/thumbnail`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.blob();
  };

  getEmailWithAnalysis = async (emailId: string): Promise<any> => {
    let res = await this._fetch({
      url: `/emails/${emailId}/analysis`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Transcription endpoints
  getEmailTranscriptions = async (emailId: string): Promise<any[]> => {
    let res = await this._fetch({
      url: `/emails/${emailId}/transcriptions`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getAttachmentTranscription = async (
    emailId: string,
    attachmentName: string,
  ): Promise<any> => {
    let res = await this._fetch({
      url: `/emails/${emailId}/attachments/${encodeURIComponent(attachmentName)}/transcription`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Archive and read operations
  archiveEmail = async (emailId: string): Promise<Email> => {
    let res = await this._fetch({
      url: `/emails/${emailId}/archive`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  unarchiveEmail = async (emailId: string): Promise<Email> => {
    let res = await this._fetch({
      url: `/emails/${emailId}/unarchive`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  archiveEmailsBatch = async (emailIds: string[]): Promise<void> => {
    await this._fetch({
      url: "/emails/archive-batch",
      method: "POST",
      bodyMime: "application/json",
      body: { email_ids: emailIds },
      auth: ["OAuth2PasswordBearer"],
    });
  };

  markEmailAsRead = async (emailId: string): Promise<Email> => {
    let res = await this._fetch({
      url: `/emails/${emailId}/mark-read`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  markEmailAsUnread = async (emailId: string): Promise<Email> => {
    let res = await this._fetch({
      url: `/emails/${emailId}/mark-unread`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getEmailsWithPreOrders = async (options?: {
    page?: number;
    size?: number;
    include_preorders?: boolean;
    include_archived?: boolean;
    archived_only?: boolean;
    search?: string;
    modified_since?: string;
  }): Promise<EmailList> => {
    let res = await this._fetch({
      url: "/emails/",
      method: "GET",
      // Pre-order linkage now comes from the Dexie-derived counts hook
      // (`usePreOrderStatusCounts`); skip the legacy bundling so the server
      // doesn't run those joins on every email-list fetch.
      params: { ...options, include_preorders: false },
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postEmailsIdTriggerWorkflow = async (
    emailId: string,
    body?: { workflow_id?: string; instructions?: string },
  ): Promise<any> => {
    let res = await this._fetch({
      url: `/emails/${emailId}/trigger-workflow`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
      body: body,
      bodyMime: body ? "application/json" : undefined,
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Email address/domain AI context
  getEmailAddressContext = async (
    emailAddress: string,
  ): Promise<EmailContextForAddressResponse> => {
    let res = await this._fetch({
      url: "/emails/address-context",
      method: "GET",
      params: { email_address: emailAddress },
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  upsertEmailAddressContext = async (body: {
    email_address?: string | null;
    domain?: string | null;
    ai_context?: string | null;
  }): Promise<EmailAddressContextResponse> => {
    let res = await this._fetch({
      url: "/emails/address-context",
      method: "PUT",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
